import type { JSX } from 'solid-js'
import { For } from 'solid-js'
import { Trans } from '@mbarzda/solid-i18next'

export type ToggleGroupOption<T extends string> =
  | { value: T; labelKey: string; label?: never }
  // Plain label for texts that come from the API (e.g. tier names) instead of i18n
  | { value: T; label: string; labelKey?: never }

export interface ToggleGroupProps<T extends string> {
  options: ToggleGroupOption<T>[]
  value: T
  onChange: (value: T) => void
}

/**
 * Segmented control: one shared neutral track with the active item filled in the dashboard's
 * blue. Reads as a single filter with one selection, unlike detached buttons where the
 * inactive ones look individually clickable and the active one looks like a submit action.
 */
function ToggleGroup<T extends string>(props: ToggleGroupProps<T>): JSX.Element {
  return (
    <div class="inline-flex items-center gap-0.5 rounded-lg border border-gray-300 bg-gray-200 p-1" role="tablist">
      <For each={props.options}>
        {(option) => (
          <button
            type="button"
            role="tab"
            aria-selected={props.value === option.value}
            onClick={() => props.onChange(option.value)}
            class="rounded-md px-3 py-1.5 text-sm font-medium transition-colors duration-150"
            classList={{
              'bg-blue-600 text-white shadow-sm': props.value === option.value,
              'text-gray-500 hover:bg-white/60 hover:text-gray-900': props.value !== option.value
            }}
          >
            {option.labelKey ? <Trans key={option.labelKey} /> : option.label}
          </button>
        )}
      </For>
    </div>
  )
}

export default ToggleGroup
